import { UserApi } from './../api/user.api';
import { User } from './../utils/models/user.model';
import { Injectable } from '@angular/core';
import { StorageV2Service, ToastV2Service } from '@schenck/rotec-components';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, share, switchMap, tap } from 'rxjs/operators';
import { TenantService } from './tenant.service';
import { BaseService } from './base.service';
import { SpinnerService } from './spinner-service/spinner.service';
import { USER_GRID_DEFAULT_FILTERS } from 'src/utils/constants/users.const';

const serviceKey = 'User';

@Injectable()
export class UserService extends BaseService<User> {
  protected filterMapFn = USER_GRID_DEFAULT_FILTERS;

  constructor(
    protected storageService: StorageV2Service,
    protected userApi: UserApi,
    protected tenantService: TenantService,
    protected toastService: ToastV2Service,
    protected spinnerService: SpinnerService
  ) {
    super(storageService, toastService, userApi, serviceKey, spinnerService);
    this.initGridState();
  }

  public exportUsersAsCSV(): void {
    this.handleCSVExport(this.userApi.exportUsersAsCSV(), 'users');
  }

  protected subsribeToGridParamsChanges(): void {
    this._gridParams$
      .pipe(
        filter((params) => !!params),
        tap(() => this.setLoading(true)),
        switchMap(() => combineLatest([this.getUsers$(), this.tenantService.results$])),
        filter(([_, tenants]) => !!tenants),
        map(([users, _]) => this.getUsersWithTenantName(users)),
        catchError(() => {
          this._results$.next([]);

          return of([]);
        })
      )
      .subscribe((users: User[]) => {
        this.setLoading(false);
        this._results$.next(users);
        this.patchState('loaded users grid results', this.getResults());
      });
  }

  protected getUsers$(): Observable<User[]> {
    return this.userApi.get$().pipe(share());
  }

  public getUser$(id: string): Observable<User> {
    return this.userApi.getUserById$(id);
  }

  private getUsersWithTenantName(users: User[]): User[] {
    return users.map((user) => ({
      ...user,
      tenantName: this.tenantService.getTenantNameById(user.tenantId),
    }));
  }
}
